import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import Dialog from '../../Common/Dialog/Dialog'
import DialogActions from '../../Common/Dialog/DialogActions'
import DialogButton from '../../Common/Buttons/DialogButton'
import DialogContent from '../../Common/Dialog/DialogContent'
import DialogTitle from '../../Common/Dialog/DialogTitle'
import { useAppDispatch } from '../../Common/_hooks/useAppDispatch'
import { useAppSelector } from '../../Common/_hooks/useAppSelector'
import { ClearProductDesignSelectedProduct } from '../../UIData/_actions/Account/AccountStoreActions'
import { EditProductListing, ResetProductListings } from '../../UserData/_actions/Store/StoreProductListingActions'
import { FetchUserStorePages } from '../../UserData/_actions/Store/UserStorePageActions'
import { FetchUserStorePresets } from '../../UserData/_actions/Store/UserStorePresetActions'
import { FetchPaginationItems } from '../../UserData/_actions/UserDataActions'
import type { BatchProductDesignSelection } from '../../UserData/_actions/ProductDesignActions'
import type { UserProductDesignPayload } from '../../UserData/_stores/UserProductDesignStore'
import EditProduct from '../Stores/AddProductListing/EditProduct'
import EditSelectedProducts from '../Stores/AddProductListing/EditSelectedProducts'
import Publishing from '../Stores/AddProductListing/Publishing'
import SelectStore from '../Stores/AddProductListing/SelectStore'
import buildDefaultProductListing from '../Stores/AddProductListing/Utils/buildDefaultProductListing'

interface Props {
  opened: boolean
  selection: BatchProductDesignSelection
  selectionCount: number
  totalMatchingCount: number
  onClose: () => void
}

const STEPS = ['SelectStore', 'EditSelectedProducts', 'EditDetails', 'Publishing']

const appendSelectionFilters = (formData: FormData, selection: BatchProductDesignSelection) => {
  if (selection.filters?.search && selection.filters.search.length > 0) {
    formData.append('search', selection.filters.search.join(','))
  }

  if (selection.filters?.categories && selection.filters.categories.length > 0) {
    formData.append('categories', selection.filters.categories.join(','))
  }

  if (selection.filters?.collections && selection.filters.collections.length > 0) {
    formData.append('collections', selection.filters.collections.join(','))
  }
}

const buildProductDesignFetchData = (
  selection: BatchProductDesignSelection,
  totalMatchingCount: number,
) => {
  const formData = new FormData()
  formData.append('offset', '0')

  if (selection.selectAll) {
    formData.append('limit', String(totalMatchingCount))
    appendSelectionFilters(formData, selection)
    return formData
  }

  formData.append('limit', '0')
  selection.ids.forEach(productDesignId => {
    formData.append('ids[]', String(productDesignId))
  })

  return formData
}

export default function PublishSelectedProductDesignsDialog(props: Props) {
  const [t] = useTranslation('account-v2')
  const dispatch = useAppDispatch()

  const products = useAppSelector(state => state.get('appData').get('products'))
  const productDesigns = useAppSelector(state => state.get('userData').get('productDesigns').get('data'))
  const storePresets = useAppSelector(state => state.get('userData').get('storePresets'))
  const listings = useAppSelector(state => state.get('UIData').get('account').get('storeProductListings'))

  const [currentStep, setCurrentStep] = useState(0)
  const [selectedStore, setSelectedStore] = useState('')
  const [closeDialogOpened, setCloseDialogOpened] = useState(false)
  const [publishBatchId, setPublishBatchId] = useState('')
  const [productDesignIds, setProductDesignIds] = useState<number[]>([])
  const [productDesignsLoaded, setProductDesignsLoaded] = useState(false)
  const [storePresetsLoaded, setStorePresetsLoaded] = useState(false)
  const [listingInitialized, setListingInitialized] = useState(false)

  const resetDialogState = useCallback(() => {
    setCurrentStep(0)
    setSelectedStore('')
    setCloseDialogOpened(false)
    setPublishBatchId('')
    setProductDesignIds([])
    setProductDesignsLoaded(false)
    setStorePresetsLoaded(false)
    setListingInitialized(false)
    dispatch(ResetProductListings())
  }, [dispatch])

  const handleCloseModal = useCallback(() => {
    setCloseDialogOpened(false)
    props.onClose()
  }, [props.onClose])

  const onCloseDialogOpen = useCallback(() => setCloseDialogOpened(true), [])
  const onCloseDialogClose = useCallback(() => setCloseDialogOpened(false), [])

  const onDialogClose = useCallback(() => {
    if (currentStep > 0 && currentStep < STEPS.length - 1) {
      onCloseDialogOpen()
      return
    }

    handleCloseModal()
  }, [currentStep, handleCloseModal, onCloseDialogOpen])

  const onSelectStore = useCallback((store: string) => {
    setSelectedStore(store)
    setCurrentStep(1)
  }, [])

  const onPublishStarted = useCallback((batchId: string) => {
    setPublishBatchId(batchId)
    dispatch(ClearProductDesignSelectedProduct())
  }, [dispatch])

  useEffect(() => {
    if (!props.opened) {
      return
    }

    resetDialogState()

    dispatch(FetchPaginationItems(
      'add-to-store-product-designs',
      buildProductDesignFetchData(props.selection, props.totalMatchingCount),
      'productDesigns',
    ).set({
      onSuccess: (response) => {
        const excludedIds = new Set(props.selection.excludeIds ?? [])
        const ids = response.data.items
          .map((item: UserProductDesignPayload) => item.id)
          .filter(id => !excludedIds.has(id))

        setProductDesignIds(ids)
        setProductDesignsLoaded(true)
      },
      onFailure: () => setProductDesignsLoaded(true),
    }))

    dispatch(FetchUserStorePresets().set({
      onSuccess: () => setStorePresetsLoaded(true),
      onFailure: () => setStorePresetsLoaded(true),
    }))
  }, [dispatch, props.opened, props.selection, props.totalMatchingCount, resetDialogState])

  useEffect(() => {
    if (!props.opened || selectedStore !== 'squarespace') {
      return
    }

    dispatch(FetchUserStorePages(selectedStore))
  }, [dispatch, props.opened, selectedStore])

  useEffect(() => {
    if (!props.opened || listingInitialized || !productDesignsLoaded || !storePresetsLoaded) {
      return
    }

    productDesignIds.forEach(productDesignId => {
      const productDesign = productDesigns.get(String(productDesignId))
      if (!productDesign) {
        return
      }

      const product = products.get(String(productDesign.get('id_product')))
      if (!product) {
        return
      }

      const listing = buildDefaultProductListing(productDesign, product, storePresets.get(productDesign.get('id_product')))

      dispatch(EditProductListing(
        listing.id_product_design,
        listing.id_product,
        listing.title,
        listing.description,
        listing.price,
        listing.update_preset,
        listing.is_modified,
        listing.disabled_preview_ids,
        listing.disabled_option_ids,
        listing.variant_prices,
        undefined,
      ))
    })

    setListingInitialized(true)
  }, [
    dispatch,
    listingInitialized,
    productDesignIds,
    productDesigns,
    productDesignsLoaded,
    products,
    props.opened,
    storePresets,
    storePresetsLoaded,
  ])

  const body = useMemo(() => {
    switch (currentStep) {
      case 0:
        return <SelectStore
          ready={listingInitialized && listings.count() > 0}
          onSelectStore={onSelectStore}
          onDialogClose={onDialogClose}
        />
      case 1:
        return <EditSelectedProducts
          store={selectedStore}
          index={currentStep}
          handleStep={setCurrentStep}
          onPublishStarted={onPublishStarted}
          onDialogClose={onDialogClose}
          allowProductRemoval={false}
          backButtonLabel={t('Back to Select store')}
        />
      case 2:
        return <EditProduct
          store={selectedStore}
          index={currentStep}
          handleStep={setCurrentStep}
          onDialogClose={onDialogClose}
          onDialogCloseWithConfirm={onDialogClose}
          showStorePageField={selectedStore === 'squarespace'}
        />
      case 3:
        return <Publishing
          store={selectedStore}
          batchId={publishBatchId}
          onDialogClose={onDialogClose}
        />
      default:
        return null
    }
  }, [
    currentStep,
    listingInitialized,
    listings,
    onDialogClose,
    onPublishStarted,
    onSelectStore,
    publishBatchId,
    selectedStore,
    t,
  ])

  const isSmallDialog = currentStep === 0 || currentStep === STEPS.length - 1;

  return <>
    <Dialog
      open={props.opened}
      maxWidth={isSmallDialog ? 'sm' : 'lg'}
      fullWidth
      onClose={onDialogClose}
      TransitionProps={{
        onExited: resetDialogState,
      }}
      showCloseButton
      hasTabs={currentStep === 2}
    >
      {body}
    </Dialog>

    <Dialog
      open={closeDialogOpened}
      maxWidth="xs"
      fullWidth
      onClose={onCloseDialogClose}
      showCloseButton
    >
      <DialogTitle>{t('Are you sure?')}</DialogTitle>
      <DialogContent>
        <p>{t('You will lose your changes if you close this dialog.')}</p>
      </DialogContent>
      <DialogActions>
        <DialogButton
          color="secondary"
          onClick={onCloseDialogClose}
        >{t('Cancel')}</DialogButton>
        <DialogButton
          color="primary"
          onClick={handleCloseModal}
          autoFocus
        >{t('Close')}</DialogButton>
      </DialogActions>
    </Dialog>
  </>
}
